import { createRouter, createWebHistory } from '@ionic/vue-router'
import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationPathRaw,
  RouteRecordRaw,
  RouteRecordSingleView
} from 'vue-router'
import { trackingEventViewPage } from '~/composables/tracking'
import { getTrackingAttributionFromQuery } from '~/utils/trackingAttribution'
import { gaEventLaunchFromLink } from '~/composables/analytics'
import middlewareAuth from '~/router/middleware/auth'
import {
  useMenuStore,
  useNavigationStore,
  useOrderStore,
  useProductStore,
  useProductChoiceStore,
  useRestaurantStore,
  useTrackStore
} from '~/store'
import { OnlineOrderType } from '~/types/restaurant'
import { parseEmbeddedRoute, parseStringToQueryParams } from '~/utils/string'
import { enableMadBunday } from '~/constants/madBunday'
import * as Sentry from '@sentry/capacitor'

/**
 * @description Web app routes.
 */
const routes: Readonly<RouteRecordRaw[]> = [
  {
    name: 'embed',
    path: '/embed',
    beforeEnter: (to: RouteLocationNormalized, _from: RouteLocationNormalizedLoaded, next: NavigationGuardNext) => {
      Sentry.addBreadcrumb({
        category: 'router',
        message: `Entering /embed route: ${to.fullPath}`,
        level: 'info',
        data: { query: to.query }
      })
      const orderStore = useOrderStore()
      const restaurantStore = useRestaurantStore()
      const menuStore = useMenuStore()
      const productStore = useProductStore()
      const productChoiceStore = useProductChoiceStore()
      const trackStore = useTrackStore()
      const navigationStore = useNavigationStore()
      // Send messages to marketing site
      emitSignedInState()
      emitUserTags()
      emitCartItemCount()

      // Clear previous navigation store state
      navigationStore.$reset()

      const parsedPath = parseEmbeddedRoute(to.query.goto as string)

      const response: RouteLocationPathRaw = {
        path: parsedPath,
        query: {
          ...parseStringToQueryParams(parsedPath),
          from: 'embed',
          /**
           * @description The sign-up source attribute to send when user signs up.
           * This is used to track where the user signed up from.
           */
          r: to.query.r,
          source: to.query.source,
          promo: to.query.promo
        }
      }

      function handleQueryParameters(): void {
        navigationStore.resetSignUpExperience()
        navigationStore.resetReferralCode()
        const referralCode = to.query.code instanceof Array ? to.query.code[0] : to.query.code
        navigationStore.referralCode = referralCode?.toString()
        const experienceTag = to.query.gexp instanceof Array ? to.query.gexp[0] : to.query.gexp
        navigationStore.experienceTag = experienceTag?.toString()
      }
      function handleWhitelistedRoutes(): void {
        // Whitelist routes that are allowed to be accessed from the parent site
        const whitelistedRoutes = [
          'home',
          'order',
          'menu',
          'restaurants',
          'profile',
          'relish',
          'sign-in',
          'sign-up',
          'reset-password',
          'catering',
          'order-at-table'
        ]
        const urlSlug = parsedPath.split('/').filter(Boolean)
        if (!urlSlug[0] || !whitelistedRoutes.includes(urlSlug[0])) {
          console.warn('Unknown route:', parsedPath)
          response.path = '/home'
        } else if (urlSlug[0] === 'order') {
          // Redirect to home if order route is accessed
          response.path = '/home'
        }
      }

      function handlePushRoute(): void {
        if (parsedPath.includes('home') && to.query.push) {
          /**
           * Gets the page to push to from url path
           * @example /embed?goto=/home&push=history
           */

          let push = to.query.push instanceof Array ? to.query.push[0] : to.query.push
          if (push) {
            if (!push.startsWith('/')) {
              push = `/${push}`
            }

            switch (push) {
              case '/history': {
                navigationStore.pushTo = push
                const orderId = to.query.orderId instanceof Array ? to.query.orderId[0] : to.query.orderId
                if (orderId) {
                  navigationStore.orderId = Number(orderId)
                }
                break
              }
              case '/config': {
                navigationStore.pushTo = push
                const restaurantId = to.query.restaurant instanceof Array ? to.query.restaurant[0] : to.query.restaurant
                const orderType = to.query.orderType instanceof Array ? to.query.orderType[0] : to.query.orderType

                if (restaurantId) {
                  navigationStore.temporaryRestaurantId = Number(restaurantId)
                }
                if (orderType) {
                  navigationStore.temporaryOrderType = Number(orderType)
                }
                break
              }
              case '/profile':
              case '/relish':
              case '/profile/edit':
              case '/profile/burger-pass':
              case '/profile/device-permissions': {
                navigationStore.pushTo = push
                break
              }
              case '/profile/mad-bunday': {
                // push to the mad-bunday page, falling back to the profile page if mad-bunday is disabled
                if (enableMadBunday) {
                  navigationStore.pushTo = push
                } else {
                  navigationStore.pushTo = '/profile'
                }
                break
              }
              case '/profile/referrals': {
                navigationStore.pushTo = '/profile/referrals'
                break
              }
              case '/student-sign-up': {
                navigationStore.pushTo = '/profile/edit/student-sign-up'
                break
              }
              default: {
                console.warn('Unknown push route:', push)
                break
              }
            }
            navigationStore.pushFrom = '/home'
          }

          response.path = '/home'
        }
      }

      function handlePromoRoute(): void {
        if (parsedPath.includes('home') && to.query.promo) {
          /**
           * Gets the promo to push to from url path
           * @example /embed?goto=/home&push=history
           */

          const promo = to.query.promo instanceof Array ? to.query.promo[0] : to.query.promo
          if (promo) {
            navigationStore.modalTo = 'promo'
            navigationStore.modalFrom = '/home'
            navigationStore.promoId = Number(promo)
          }

          response.path = '/home'
        }
      }

      function handleModalRoute(): void {
        if (parsedPath.includes('home') && to.query.modal) {
          /**
           * Gets the modal to push to from url path
           * @example /embed?goto=/home&modal=verification-modal
           * other routes such as promo and sign-up and sign-in are handled separately
           */

          const modal = to.query.modal instanceof Array ? to.query.modal[0] : to.query.modal
          if (modal === 'verification-modal') {
            navigationStore.modalTo = 'verification-modal'
            navigationStore.modalFrom = '/home'
          }

          response.path = '/home'
        }
      }
      // Handle setting the restaurant by id from query param in the URL
      function handleRestaurantIdQuery(): void {
        if (to.query.restaurant) {
          try {
            const restaurantId = Number(to.query.restaurant)
            if (!restaurantStore.restaurant?.id || restaurantStore.restaurant?.id !== restaurantId) {
              // TODO GRIL-412 improvement: rather than clearing cart here, perform full validation to validate the cart
              // and remove any items that are not valid for the selected restaurant
              orderStore.clearCartAndOrder()
              // also reset the menu so it will be re-fetched
              menuStore.resetMenu()
              productStore.clearStore()
              productChoiceStore.clearStores()
              if (restaurantId) {
                navigationStore.restaurantId = restaurantId
                return
              }
            }
          } catch (e) {
            console.error(`Could not find restaurant with ID from URL query param.`)
          }
        }
      }

      function handleCateringRoute(): void {
        // Handle routing to Catering
        if (parsedPath === '/catering' || parsedPath === 'catering') {
          restaurantStore.isCateringMode = true
          if (restaurantStore.orderConfig.orderType !== OnlineOrderType.Catering) {
            restaurantStore.resetOrderTypeAndTime()
            // clear the cart as it is not valid after switching to catering mode
            orderStore.clearCartAndOrder()
            // clear the existing restaurant if not specified in the query parameters
            if (!to.query.restaurant) {
              restaurantStore.clearSelectedRestaurant()
            } else {
              navigationStore.isInitialisingNavigation.catering = true
            }
          }
        } else {
          restaurantStore.isCateringMode = false
          if (restaurantStore.orderConfig.orderType === OnlineOrderType.Catering) {
            restaurantStore.resetOrderTypeAndTime()
            // clear the cart as it is not valid after switching from catering mode
            orderStore.clearCartAndOrder()
          }
        }
      }

      /**
       * @description Handle routing to menu category
       * @example /embed?goto=/menu/category/1
       * @example /embed?goto=/menu/category/1&restaurant=1
       */
      function handleMenuCategoryRoute(): void {
        if (parsedPath.includes('menu') && parsedPath.includes('category')) {
          /**
           * Gets the category ID from url path
           * @example /embed?goto=/menu/category/1
           */
          const categoryId = to.query.goto?.includes('category')
            ? Number(to.query.goto?.slice(to.query.goto?.lastIndexOf('/') + 1))
            : null

          if (categoryId) {
            navigationStore.categoryId = categoryId
            navigationStore.pushTo = '/menu'
            navigationStore.pushFrom = '/home'
            response.path = '/home'
            return
          }

          console.warn('Unable to set category. Fall back to /home')
          response.path = '/home'
        }
      }
      function handleMenuProductRoute(): void {
        if (parsedPath.includes('menu') && parsedPath.includes('product')) {
          /**
           * Gets the product ID from url path
           * @example /embed?goto=/menu/product/1
           */
          const productId = to.query.goto?.includes('product')
            ? Number(to.query.goto?.slice(to.query.goto?.lastIndexOf('/') + 1))
            : null

          if (productId) {
            navigationStore.productId = productId
            navigationStore.pushTo = 'menu-product'
            navigationStore.pushFrom = '/home'
            response.path = '/home'
            return
          }

          console.warn('Unable to set product. Fall back to /home')
          response.path = '/home'
        }
      }

      function handleAccountJourneyType(): void {
        if (!to.query.journeyType) {
          to.query.journeyType = 'marketing_site'
        }

        if (to.query.goto === '/sign-in') {
          gaEventSignInStart(to.query.journeyType as string)
        }
        if (to.query.goto === '/sign-up') {
          gaEventSignUpStart(to.query.journeyType as string)
        }
      }

      function handleClickIds(): void {
        // Retrieve facebook and tiktok click ids from query params
        if (to.query.fbclid) {
          const fbclid = to.query.fbclid as string
          trackStore.setMetaClickId(fbclid)
        }
        if (to.query.ttclid) {
          const ttclid = to.query.ttclid as string
          trackStore.setTikTokClickId(ttclid)
        }
      }

      const attribution = getTrackingAttributionFromQuery(
        to.query as Record<string, string | string[] | null | undefined>
      )
      if (attribution) {
        trackStore.setAttribution(attribution)
      } else {
        trackStore.getActiveAttribution()
      }

      handleQueryParameters()
      handleWhitelistedRoutes()
      handleClickIds()
      handlePushRoute()
      handlePromoRoute()
      handleModalRoute()
      handleRestaurantIdQuery()
      handleMenuCategoryRoute()
      handleMenuProductRoute()
      handleCateringRoute()
      handleAccountJourneyType()
      if (response.path === '/home') {
        navigationStore.isInitialisingNavigation.home = true
      }
      // If the panel was opened from an applink, track the event
      if (to.query.applink) {
        gaEventLaunchFromLink('applink-web-fallback', parsedPath)
      }

      Sentry.addBreadcrumb({
        category: 'router',
        message: `Parsed embedded route: ${to.query.goto}`,
        level: 'debug',
        data: { parsedPath, response }
      })

      next(response)
    }
  } as RouteRecordSingleView,
  {
    path: '/',
    redirect: '/home'
  },
  {
    name: 'home',
    path: '/home',
    component: () => import('~/views/home/ViewHome.vue')
  },
  {
    name: 'config',
    path: '/config',
    component: () => import('~/views/order/ViewOrderConfig.vue')
  },
  {
    name: 'order-at-table',
    path: '/order-at-table/:restaurantId',
    component: () => import('~/views/order/ViewOrderAtTable.vue')
  },
  {
    name: 'catering',
    path: '/catering',
    component: () => import('~/views/order/ViewCateringOrderConfig.vue')
  },
  {
    name: 'restaurants',
    path: '/restaurants',
    component: () => import('~/views/restaurants/ViewRestaurants.vue')
  },
  {
    name: 'restaurant-detail',
    path: '/restaurants/:id',
    component: () => import('~/views/restaurants/ViewRestaurantItem.vue')
  },
  {
    name: 'menu-search',
    path: '/menu/search',
    component: () => import('~/views/menu/ViewMenuSearch.vue')
  },
  {
    name: 'menu',
    path: '/menu',
    component: () => import('~/views/menu/ViewMenu.vue')
  },
  {
    name: 'menu-product',
    path: '/menu/product/:id',
    component: () => import('~/views/menu/product/ViewProductItem.vue'),
    props: (route) => ({
      id: route.params.id,
      title: route.query?.title
    })
  },
  {
    name: 'menu-product-customisation',
    path: '/menu/product/:id/customise',
    component: () => import('~/views/menu/product/ViewProductItemCustomise.vue')
  },
  {
    name: 'menu-product-nutrition',
    path: '/menu/product/:id/nutritionalinfo',
    component: () => import('~/views/menu/product/ViewProductItemNutrition.vue')
  },
  {
    name: 'menu-product-list',
    path: '/menu/product/:id/productchoices',
    component: () => import('~/views/menu/product/ViewProductList.vue'),
    props: (route) => ({
      parentStoreId: route.query?.parentStoreId
    })
  },
  {
    name: 'menu-product-custom',
    path: '/menu/customproduct/:id',
    component: () => import('~/views/menu/product/ViewCustomProductItem.vue'),
    props: (route) => ({
      id: route.params.id,
      title: route.query?.title,
      baseIsFree:
        route.query?.baseIsFree instanceof Array
          ? route.query.baseIsFree[0]?.toLowerCase() === 'true'
          : route.query.baseIsFree?.toLowerCase() === 'true',
      storeId: route.query?.storeId,
      parentStoreId: route.query?.parentStoreId
    })
  },
  {
    name: 'menu-product-custom-customisation',
    path: '/menu/customproduct/:id/customise',
    component: () => import('~/views/menu/product/ViewCustomProductItemCustomise.vue'),
    props: (route) => ({
      storeId: route.query?.storeId
    })
  },
  {
    name: 'menu-product-custom-nutrition',
    path: '/menu/customproduct/:id/nutritionalinfo',
    component: () => import('~/views/menu/product/ViewCustomProductItemNutrition.vue'),
    props: (route) => ({
      storeId: route.query?.storeId
    })
  },
  {
    name: 'checkout',
    path: '/checkout',
    component: () => import('~/views/checkout/ViewCheckout.vue'),
    props: (route) => ({ replaceBackPath: route.query.replaceBackPath })
  },
  {
    name: 'history',
    path: '/history',
    component: () => import('~/views/order/ViewOrderHistory.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'history-item',
    path: '/history/:id',
    component: () => import('~/views/order/ViewOrderHistoryItem.vue')
  },
  {
    name: 'external-page',
    path: '/external',
    props: (route) => ({
      pageUrl: route.query?.pageUrl,
      pageTitle: route.query?.title
    }),
    component: () => import('~/views/ViewExternalPage.vue')
  },
  {
    name: 'profile',
    path: '/profile',
    component: () => import('~/views/profile/ViewSettings.vue')
  },
  {
    name: 'mad-bunday',
    path: '/profile/mad-bunday',
    component: () => import('~/views/profile/mad-bunday/ViewMadBunday.vue'),
    props: (route) => ({ source: route.query?.source, promo: route.query?.promo, referrer: route.query?.r }),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'edit-profile',
    path: '/profile/edit',
    component: () => import('~/views/profile/edit/ViewEditProfile.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'edit-password',
    path: '/profile/edit/password',
    component: () => import('~/views/profile/edit/ViewEditPassword.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'student-sign-up',
    path: '/profile/edit/student-sign-up',
    component: () => import('~/views/profile/edit/ViewStudentSignUp.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'student-verification',
    path: '/profile/student-verification',
    component: () => import('~/views/profile/edit/StudentVerification/ViewStudentVerification.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'reset-password',
    path: '/reset-password',
    component: () => import('~/views/login/ViewResetPassword.vue')
  },
  {
    name: 'comms-preferences',
    path: '/profile/comms/comms-preferences',
    component: () => import('~/views/profile/comms/ViewCommsPreferences.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'burger-pass',
    path: '/profile/burger-pass',
    component: () => import('~/views/profile/subscriptions/ViewBurgerPass.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    name: 'referrals',
    path: '/profile/referrals',
    component: () => import('~/views/profile/referrals/ViewReferrals.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'relish',
    path: '/relish',
    component: () => import('~/views/relish/ViewRelish.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'relish-coupon-item',
    path: '/relish/coupons/:id',
    component: () => import('~/views/relish/coupons/ViewCouponItem.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    name: 'sign-in',
    path: '/sign-in',
    component: () => import('~/views/login/ViewSignIn.vue')
  },
  {
    name: 'sign-up',
    path: '/sign-up',
    component: () => import('~/views/login/ViewSignUp.vue')
  },
  {
    name: 'sign-up-details',
    path: '/sign-up/details',
    beforeEnter: (to, from, next) => {
      if (!from.name) {
        next({ name: 'sign-up' })
      } else {
        next()
      }
    },
    component: () => import('~/views/login/ViewSignUpDetails.vue')
  },
  {
    name: 'not-found',
    path: '/:pathMatch(.*)*',
    redirect: '/home'
  }
]

const webRouter = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_URL),
  routes
})

webRouter.beforeEach((to, from) => {
  Sentry.addBreadcrumb({
    category: 'router',
    message: `Navigating from ${from.fullPath} to ${to.fullPath}`,
    type: 'navigation',
    level: 'debug',
    data: { from: from.fullPath, to: to.fullPath, params: to.params, query: to.query }
  })
  // Route Guard: if not authenticated, redirect to /home.
  if (to.meta.requiresAuth) {
    const redirect = middlewareAuth()

    if (redirect) {
      return redirect
    }
  }
})

webRouter.afterEach((to, from) => {
  Sentry.addBreadcrumb({
    category: 'router',
    message: `Navigation complete to ${to.fullPath}`,
    type: 'navigation',
    level: 'debug',
    data: { to: to.fullPath, params: to.params, query: to.query, from: from.fullPath }
  })
  trackingEventViewPage({ content_name: to.path })
})

export default webRouter
